import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, ArrowUpRight, Calendar, Check, ImageIcon, Loader2, Plus,
} from 'lucide-react';
import { Layout } from '../components/layout';
import { OrthoDrawing } from '../components/cases';
import { Badge, Button, Modal } from '../components/ui';
import { useCase } from '../hooks/useCases';
import { useSEO } from '../hooks/useSEO';
import { useAppStore } from '../context/store';
import {
  formatDimension,
  formatDimensionValue,
  formatPrice,
  formatWeight,
  calculateVolumeLiters,
  getAmazonSearchUrl,
} from '../utils/conversions';
import { sortFormFactors } from '../utils/formFactors';

const DRAWING_SCALE = 0.42;

export const CaseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { pcCase, loading, error } = useCase(id || null);
  const { addToComparison, removeFromComparison, isInComparison, comparison } = useAppStore();

  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';
  const inComparison = id ? isInComparison(id) : false;
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const pageUrl = typeof window !== 'undefined' ? `${window.location.origin}/case/${id}` : '';

  useSEO({
    title: pcCase ? `${pcCase.manufacturer} ${pcCase.name}` : 'PC Case Details',
    description: pcCase
      ? `${pcCase.manufacturer} ${pcCase.name} - ${pcCase.sizeCategory} PC case. ${pcCase.dimensions ? `${pcCase.dimensions.height}×${pcCase.dimensions.width}×${pcCase.dimensions.depth}mm` : ''}. ${pcCase.formFactorSupport.join(', ')} support. Max GPU ${pcCase.maxGpuLength}mm, CPU cooler ${pcCase.maxCpuCoolerHeight}mm.`
      : undefined,
    image: pcCase?.imageUrl || pcCase?.thumbnailUrl || undefined,
    url: pageUrl,
    type: 'product',
  });

  // JSON-LD structured data
  useEffect(() => {
    if (!pcCase) return;

    const productJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: `${pcCase.manufacturer} ${pcCase.name}`,
      description: pcCase.description,
      brand: { '@type': 'Brand', name: pcCase.manufacturer },
      category: `PC Cases > ${pcCase.sizeCategory}`,
      url: pageUrl,
      ...(pcCase.imageUrl && { image: pcCase.imageUrl }),
      ...(pcCase.productPageUrl && { sameAs: pcCase.productPageUrl }),
      ...((() => {
        const validOffers = (pcCase.referralLinks ?? []).filter(link => link.price > 0);
        if (validOffers.length === 0) return {};
        const priceValidUntil = new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        return {
          offers: validOffers.map(link => ({
            '@type': 'Offer',
            url: link.url,
            priceCurrency: link.currency || 'USD',
            price: link.price,
            priceValidUntil,
            availability: 'https://schema.org/InStock',
            seller: { '@type': 'Organization', name: link.retailer },
          })),
        };
      })()),
      additionalProperty: [
        { '@type': 'PropertyValue', name: 'Form Factor', value: pcCase.formFactorSupport.join(', ') },
        { '@type': 'PropertyValue', name: 'Max GPU Length', value: `${pcCase.maxGpuLength}mm` },
        { '@type': 'PropertyValue', name: 'Max CPU Cooler Height', value: `${pcCase.maxCpuCoolerHeight}mm` },
        { '@type': 'PropertyValue', name: 'Max PSU Length', value: `${pcCase.maxPsuLength}mm` },
        ...(pcCase.dimensions ? [
          { '@type': 'PropertyValue', name: 'Height', value: `${pcCase.dimensions.height}mm` },
          { '@type': 'PropertyValue', name: 'Width', value: `${pcCase.dimensions.width}mm` },
          { '@type': 'PropertyValue', name: 'Depth', value: `${pcCase.dimensions.depth}mm` },
        ] : []),
      ],
    };

    const breadcrumbJsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://componentscale.com' },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${pcCase.manufacturer} ${pcCase.name}`,
          item: pageUrl,
        },
      ],
    };

    const productScript = document.createElement('script');
    productScript.type = 'application/ld+json';
    productScript.textContent = JSON.stringify(productJsonLd);
    document.head.appendChild(productScript);

    const breadcrumbScript = document.createElement('script');
    breadcrumbScript.type = 'application/ld+json';
    breadcrumbScript.textContent = JSON.stringify(breadcrumbJsonLd);
    document.head.appendChild(breadcrumbScript);

    return () => {
      productScript.remove();
      breadcrumbScript.remove();
    };
  }, [pcCase]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <Loader2 className="h-7 w-7 animate-spin text-muted" aria-label="Loading case" />
        </div>
      </Layout>
    );
  }

  if (error || !pcCase) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center gap-4 py-32">
          <p className="text-muted">Case not found</p>
          <Button variant="outline" onClick={() => navigate('/')}>
            <ArrowLeft size={16} />
            Back to cases
          </Button>
        </div>
      </Layout>
    );
  }

  const dims = pcCase.dimensions;
  const image = pcCase.imageUrl || pcCase.thumbnailUrl;
  const weightUnit = unit === 'mm' ? 'kg' : 'lb';
  const comparisonFull = !inComparison && comparison.selectedCases.length >= 4;

  // Priced offers first (cheapest → most expensive), unpriced last; Amazon search as fallback
  const shopLinks = pcCase.referralLinks && pcCase.referralLinks.length > 0
    ? [...pcCase.referralLinks].sort((a, b) => (a.price || Infinity) - (b.price || Infinity))
    : [{ retailer: 'Amazon', url: getAmazonSearchUrl(pcCase.manufacturer, pcCase.name), price: 0, currency: 'USD' }];

  const toggleComparison = () => {
    if (inComparison) {
      removeFromComparison(pcCase.id);
    } else {
      addToComparison(pcCase.id);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-5xl space-y-8">
        {/* Back nav + comparison shortcut */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex h-9 items-center gap-1.5 text-sm text-muted hover:text-fg transition-colors"
          >
            <ArrowLeft size={15} />
            Back
          </button>

          {comparison.selectedCases.length > 0 && (
            <Link
              to="/compare"
              className="inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium text-muted hover:bg-surface-2 hover:text-fg transition-colors"
            >
              Open comparison
              <span className="rounded-full bg-accent px-1.5 font-mono text-xs text-accent-fg">
                {comparison.selectedCases.length}
              </span>
              <ArrowRight size={15} />
            </Link>
          )}
        </div>

        {/* Header */}
        <div className="grid gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
          <div className="relative aspect-[4/3] overflow-hidden rounded-xl border border-line bg-surface-2 bg-blueprint-sm">
            {image ? (
              <button
                type="button"
                onClick={() => setLightboxOpen(true)}
                className="absolute inset-0 cursor-zoom-in"
                aria-label="Enlarge image"
              >
                <img
                  src={image}
                  alt={`${pcCase.manufacturer} ${pcCase.name}`}
                  className="h-full w-full object-contain p-6 mix-blend-multiply dark:mix-blend-normal"
                />
              </button>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted">
                <ImageIcon size={36} />
                <span className="text-sm">No photo yet</span>
              </div>
            )}
          </div>

          <div className="flex min-w-0 flex-col gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-label text-muted">{pcCase.manufacturer}</p>
              <h1 className="mt-1 text-3xl font-semibold tracking-tightest sm:text-4xl">{pcCase.name}</h1>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Badge>{pcCase.sizeCategory}</Badge>
              {dims && <Badge><span className="font-mono">{calculateVolumeLiters(dims)} L</span></Badge>}
              {pcCase.releaseYear > 0 && (
                <Badge>
                  <Calendar size={12} />
                  <span className="font-mono">{pcCase.releaseYear}</span>
                </Badge>
              )}
            </div>

            {pcCase.description && (
              <p className="leading-relaxed text-muted">{pcCase.description}</p>
            )}

            <dl className="grid grid-cols-3 gap-4 border-y border-line py-4">
              <KeyStat label="GPU max" value={formatDimensionValue(pcCase.maxGpuLength, unit)} unit={unit} />
              <KeyStat label="Cooler max" value={formatDimensionValue(pcCase.maxCpuCoolerHeight, unit)} unit={unit} />
              <KeyStat label="PSU max" value={formatDimensionValue(pcCase.maxPsuLength, unit)} unit={unit} />
            </dl>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={inComparison ? 'accent' : 'primary'}
                size="lg"
                onClick={toggleComparison}
                disabled={comparisonFull}
                aria-pressed={inComparison}
              >
                {inComparison ? <Check size={16} /> : <Plus size={16} />}
                {inComparison ? 'In comparison' : 'Add to comparison'}
              </Button>
              {shopLinks.map((link) => (
                <a
                  key={link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer sponsored"
                  className="inline-flex h-11 items-center gap-2 rounded-lg border border-line-strong px-4 text-sm font-medium hover:border-fg hover:bg-surface-2 transition-colors"
                >
                  {link.price > 0 && <span className="font-mono">{formatPrice(link.price, link.currency)}</span>}
                  {link.retailer}
                  <ArrowUpRight size={14} />
                </a>
              ))}
              {pcCase.productPageUrl && (
                <a
                  href={pcCase.productPageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex h-11 items-center gap-1.5 px-2 text-sm text-muted hover:text-fg transition-colors"
                >
                  Manufacturer page
                  <ArrowUpRight size={14} />
                </a>
              )}
            </div>
            {comparisonFull && (
              <p className="text-sm text-accent-ink">Comparison is full — remove a case first.</p>
            )}
          </div>
        </div>

        {/* Drawing sheet */}
        {dims && (
          <section className="overflow-hidden rounded-xl border border-line bg-surface">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <h2 className="font-mono text-xs uppercase tracking-label text-muted">Drawing · front &amp; side</h2>
              <span className="font-mono text-xs text-muted">{unit}</span>
            </div>
            <div className="grid md:grid-cols-[minmax(0,1fr)_260px]">
              <div className="flex min-h-[300px] items-end justify-center overflow-x-auto border-line bg-surface-2 bg-blueprint px-6 pb-6 pt-10 md:border-r">
                <OrthoDrawing dimensions={dims} scale={DRAWING_SCALE} unit={unit} selected={inComparison} />
              </div>
              <dl className="divide-y divide-line px-4">
                <SpecRow label="Height" value={formatDimension(dims.height, unit)} />
                <SpecRow label="Width" value={formatDimension(dims.width, unit)} />
                <SpecRow label="Depth" value={formatDimension(dims.depth, unit)} />
                <SpecRow label="Volume" value={`${calculateVolumeLiters(dims)} L`} />
              </dl>
            </div>
          </section>
        )}

        {/* Spec sheet */}
        <div className="grid gap-6 md:grid-cols-2">
          <SpecCard title="Motherboard support">
            <div className="flex flex-wrap gap-2 py-3">
              {sortFormFactors(pcCase.formFactorSupport).map((ff) => (
                <Badge key={ff}>{ff}</Badge>
              ))}
            </div>
          </SpecCard>

          <SpecCard title="Details">
            <dl className="divide-y divide-line">
              <SpecRow label="Weight" value={pcCase.weight > 0 ? formatWeight(pcCase.weight, weightUnit) : '—'} />
              <SpecRow label="Side panel" value={pcCase.sidePanel.join(', ') || '—'} mono={false} />
              <SpecRow label="Colors" value={pcCase.colors.join(', ') || '—'} mono={false} />
              {pcCase.releaseYear > 0 && <SpecRow label="Release year" value={pcCase.releaseYear.toString()} />}
            </dl>
          </SpecCard>

          {pcCase.driveBays && (
            <SpecCard title="Drive bays">
              <dl className="divide-y divide-line">
                <SpecRow label='3.5" internal' value={pcCase.driveBays.internal35.toString()} />
                <SpecRow label='2.5" internal' value={pcCase.driveBays.internal25.toString()} />
                {pcCase.driveBays.external525 != null && (
                  <SpecRow label='5.25" external' value={pcCase.driveBays.external525.toString()} />
                )}
              </dl>
            </SpecCard>
          )}

          {pcCase.ioPanel && (
            <SpecCard title="Front I/O">
              <dl className="divide-y divide-line">
                {pcCase.ioPanel.usb3 != null && <SpecRow label="USB 3.0" value={pcCase.ioPanel.usb3.toString()} />}
                {pcCase.ioPanel.usb2 != null && <SpecRow label="USB 2.0" value={pcCase.ioPanel.usb2.toString()} />}
                {pcCase.ioPanel.usbC != null && <SpecRow label="USB-C" value={pcCase.ioPanel.usbC.toString()} />}
                <SpecRow label="Audio jack" value={pcCase.ioPanel.audio ? 'Yes' : 'No'} mono={false} />
              </dl>
            </SpecCard>
          )}

          {pcCase.fanMounts.length > 0 && (
            <SpecCard title="Fan mounts" className="md:col-span-2">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[420px] text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs text-muted">
                      <th scope="col" className="py-2.5 font-normal">Location</th>
                      <th scope="col" className="py-2.5 font-normal">Fans</th>
                      <th scope="col" className="py-2.5 text-right font-normal">Radiator</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pcCase.fanMounts.map((mount, index) => (
                      <tr key={index} className="border-b border-line last:border-0">
                        <th scope="row" className="py-2.5 text-left font-medium capitalize">{mount.location}</th>
                        <td className="py-2.5 font-mono">{mount.maxFans}× {mount.sizes.join('/')} mm</td>
                        <td className="py-2.5 text-right font-mono">
                          {mount.radiatorSupport && mount.radiatorSupport.length > 0
                            ? `${mount.radiatorSupport.join('/')} mm`
                            : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SpecCard>
          )}

          {pcCase.features.length > 0 && (
            <SpecCard title="Features" className="md:col-span-2">
              <ul className="grid grid-cols-1 gap-x-6 gap-y-2.5 py-3 sm:grid-cols-2">
                {pcCase.features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-2.5 text-sm">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-[1px] bg-accent" aria-hidden="true" />
                    {feature}
                  </li>
                ))}
              </ul>
            </SpecCard>
          )}
        </div>
      </div>

      {/* Image lightbox */}
      <Modal isOpen={lightboxOpen} onClose={() => setLightboxOpen(false)} size="full" title={`${pcCase.manufacturer} ${pcCase.name}`}>
        <img
          src={image || ''}
          alt={`${pcCase.manufacturer} ${pcCase.name}`}
          className="max-h-[80vh] w-full object-contain"
        />
      </Modal>
    </Layout>
  );
};

interface KeyStatProps {
  label: string;
  value: string;
  unit: string;
}

const KeyStat: React.FC<KeyStatProps> = ({ label, value, unit }) => (
  <div className="flex flex-col gap-0.5">
    <dt className="text-xs text-muted">{label}</dt>
    <dd className="font-mono text-xl">
      {value}
      <span className="text-xs text-muted"> {unit}</span>
    </dd>
  </div>
);

interface SpecCardProps {
  title: string;
  className?: string;
  children: React.ReactNode;
}

const SpecCard: React.FC<SpecCardProps> = ({ title, className = '', children }) => (
  <section className={`rounded-xl border border-line bg-surface px-4 pt-3 pb-1 ${className}`}>
    <h2 className="border-b border-line pb-3 font-mono text-xs uppercase tracking-label text-muted">{title}</h2>
    {children}
  </section>
);

interface SpecRowProps {
  label: string;
  value: string;
  mono?: boolean;
}

const SpecRow: React.FC<SpecRowProps> = ({ label, value, mono = true }) => (
  <div className="flex items-baseline justify-between gap-4 py-2.5">
    <dt className="text-sm text-muted">{label}</dt>
    <dd className={`text-right text-sm ${mono ? 'font-mono' : ''}`}>{value}</dd>
  </div>
);
